import { useCallback, useEffect, useState } from 'react'
import { NavLink, useSearchParams } from 'react-router-dom'
import { Search } from 'lucide-react'
import { PageTitle } from '../../components/ui/PageTitle'
import { Pagination } from '../../components/ui/Pagination'
import { StatusPill } from '../../components/ui/StatusPill'
import { api } from '../../lib/api'

type Order = {
  id: number
  orderNumber: string
  full_name: string
  mobile: string
  total: number
  items_count: number
  pincode: string
  status: string
  created_at: string
}

const STATUSES = ['All', 'Placed', 'Confirmed', 'Out for Delivery', 'Delivered', 'Cancelled']

export function OrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)

  const selectedStatus = searchParams.get('status') || 'All'
  const search = searchParams.get('search') || ''
  const page = Number(searchParams.get('page') || '1')
  const limit = Number(searchParams.get('limit') || '10')

  function setSelectedStatus(s: string) {
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('status', s); n.set('page', '1'); return n })
  }
  function setSearch(s: string) {
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('search', s); n.set('page', '1'); return n })
  }
  function setPage(p: number) {
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('page', String(p)); return n })
  }
  function setLimit(l: number) {
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('limit', String(l)); n.set('page', '1'); return n })
  }

  const loadOrders = useCallback(() => {
    setLoading(true)
    const params = new URLSearchParams()
    if (selectedStatus !== 'All') params.set('status', selectedStatus)
    if (search) params.set('search', search)
    params.set('page', String(page))
    params.set('limit', String(limit))
    api.get(`/admin/orders?${params}`)
      .then(({ data }) => {
        setOrders(data.orders)
        setTotal(data.total ?? data.orders.length)
      })
      .finally(() => setLoading(false))
  }, [selectedStatus, search, page, limit])

  useEffect(() => {
    loadOrders()
  }, [loadOrders])

  useEffect(() => {
    const handleRefresh = () => {
      loadOrders()
    }

    window.addEventListener('seasonly-admin-orders-refresh', handleRefresh)
    return () => window.removeEventListener('seasonly-admin-orders-refresh', handleRefresh)
  }, [loadOrders])

  return (
    <div className="space-y-6">
      <PageTitle title={`Orders (${total})`} subtitle="Manage and track all customer orders." />

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
        <input
          className="w-full rounded-lg border border-stone-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-mango-orange focus:ring-4 focus:ring-mango-yellow/25"
          placeholder="Search by name, mobile or order ID…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div className="rounded-xl border border-stone-200 bg-white shadow-sm">
        {/* Status tabs */}
        <div className="flex flex-wrap items-center gap-2 border-b border-stone-100 p-4">
          {STATUSES.map(s => (
            <button
              key={s}
              onClick={() => setSelectedStatus(s)}
              className={`min-h-10 rounded-md border px-4 py-2 text-sm font-semibold transition ${
                selectedStatus === s
                  ? 'border-leaf-dark bg-leaf-dark text-white shadow-sm'
                  : 'border-stone-200 bg-white text-stone-700 hover:border-leaf/40 hover:bg-stone-50'
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="p-6 text-sm text-stone-500">Loading…</p>
        ) : orders.length === 0 ? (
          <p className="p-6 text-sm text-stone-500">No orders found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100 text-left text-xs font-semibold uppercase tracking-wide text-stone-400">
                  {['Order ID', 'Customer', 'Mobile', 'Items', 'Amount', 'Pincode', 'Status', 'Placed At', ''].map(h => (
                    <th key={h} className="px-5 py-4">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <tr key={order.id} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="px-5 py-5 font-bold text-leaf-dark">{order.orderNumber}</td>
                    <td className="px-5 py-5 font-medium text-stone-800">{order.full_name || '—'}</td>
                    <td className="px-5 py-5 text-stone-600">{order.mobile}</td>
                    <td className="px-5 py-5 font-bold text-stone-800">{order.items_count}</td>
                    <td className="px-5 py-5 font-semibold text-stone-800">₹{order.total}</td>
                    <td className="px-5 py-5 text-stone-600">{order.pincode}</td>
                    <td className="px-5 py-5"><StatusPill value={order.status} /></td>
                    <td className="px-5 py-5 whitespace-nowrap text-stone-500">{new Date(order.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</td>
                    <td className="px-5 py-5">
                      <NavLink to={`/orders/${order.id}`} className="inline-flex items-center rounded-lg bg-leaf-dark px-4 py-2 text-sm font-semibold text-white hover:bg-leaf transition-colors">
                        View
                      </NavLink>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination
          limit={limit}
          onLimitChange={setLimit}
          onPageChange={setPage}
          page={page}
          total={total}
        />
      </div>
    </div>
  )
}
